import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { HeartPulse, Plus, X, Pill, Trash2, ChevronDown, ChevronUp, Search } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { ComboboxInput } from '@/components/ui/ComboboxInput';
import { VademecumPickerInline, type MedicamentoSeleccionado } from '@/components/vademecum/VademecumPicker';
import { getErrorMessage } from '@/lib/errors';

/* ─ Tipos ────────────────────────────────────────────────────────────────── */
interface Medicamento {
  vademecumId?: string;
  nombre: string;
  dosis: string;
  frecuencia: string;
  duracion: string;
  via: string;
}
interface Plantilla {
  id: string;
  nombre: string;
  descripcion?: string;
  medicamentos?: Medicamento[];
  indicaciones?: string;
  activo: boolean;
  medicoId?: string;
}

const emptyMed: Medicamento = { vademecumId: '', nombre: '', dosis: '', frecuencia: '', duracion: '', via: '' };

const DOSIS_RAPIDAS = ['1/2 tableta', '1 tableta', '2 tabletas', '1 cápsula', '2 cápsulas', '5 ML', '10 ML', '1 gota', '2 gotas', '1 ampolleta'];
const FRECUENCIAS   = ['cada 4 horas', 'cada 6 horas', 'cada 8 horas', 'cada 12 horas', 'cada 24 horas', 'una vez al día', 'dos veces al día'];
const DURACIONES    = ['3 días', '5 días', '7 días', '10 días', '14 días', '21 días', '30 días', 'uso continuo'];
const VIAS          = ['oral', 'sublingual', 'tópica', 'inhalada', 'intravenosa', 'intramuscular', 'subcutánea', 'rectal', 'oftálmica', 'ótica'];

const emptyForm = { nombre: '', descripcion: '', indicaciones: '', medicamentos: [{ ...emptyMed }] };

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

export function TratamientosPage() {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();

  const [open, setOpen]         = useState(false);
  const [editing, setEditing]   = useState<Plantilla | null>(null);
  const [form, setForm]         = useState(emptyForm);
  const [expandida, setExpandida] = useState<string | null>(null);
  const [search, setSearch]     = useState('');

  const canEdit = usuario?.rolId ? ([ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO, ROL.MEDICO_ADMIN] as string[]).includes(usuario.rolId) : false;

  const { data: plantillas = [], isLoading } = useQuery({
    queryKey: ['tratamientos'],
    queryFn: () => api.get('/api/tratamientos').then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: (data: any) => api.post('/api/tratamientos', data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tratamientos'] }); toast.success('Plantilla creada'); setOpen(false); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear la plantilla')),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.patch(`/api/tratamientos/${id}`, data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tratamientos'] }); toast.success('Plantilla actualizada'); setOpen(false); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar la plantilla')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api/tratamientos/${id}`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tratamientos'] }); toast.success('Plantilla eliminada'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar la plantilla')),
  });

  function openCreate() {
    setEditing(null);
    setForm({ ...emptyForm, medicamentos: [] });
    setOpen(true);
  }

  function openEdit(p: Plantilla) {
    setEditing(p);
    setForm({
      nombre:       p.nombre || '',
      descripcion:  p.descripcion || '',
      indicaciones: p.indicaciones || '',
      medicamentos: p.medicamentos?.length ? p.medicamentos : [],
    });
    setOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      ...form,
      clinicaId: usuario?.clinicaId ?? null,
      medicamentos: form.medicamentos.filter(m => m.nombre.trim()),
    };
    if (editing) updateMut.mutate({ id: editing.id, data: payload });
    else createMut.mutate(payload);
  }

  /* ─ Helpers medicamentos en form ─ */
  function setMed(idx: number, field: keyof Medicamento, val: string) {
    setForm(p => {
      const meds = [...p.medicamentos];
      meds[idx] = { ...meds[idx], [field]: val };
      return { ...p, medicamentos: meds };
    });
  }
  function addMedFromVademecum(sel: MedicamentoSeleccionado) {
    setForm(p => ({
      ...p,
      medicamentos: [...p.medicamentos, {
        ...emptyMed,
        vademecumId: sel.vademecumId,
        nombre: sel.nombreGenerico,
        dosis: sel.dosisSugerida || '',
        frecuencia: sel.frecuenciaSugerida || '',
        via: sel.viaAdministracion || '',
      }],
    }));
  }
  function addMed() {
    setForm(p => ({ ...p, medicamentos: [...p.medicamentos, { ...emptyMed }] }));
  }
  function removeMed(idx: number) {
    setForm(p => ({ ...p, medicamentos: p.medicamentos.filter((_, i) => i !== idx) }));
  }

  const filtered = (plantillas as Plantilla[]).filter(p =>
    p.nombre.toLowerCase().includes(search.toLowerCase())
  );

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Tratamientos</h2>
          <p className="text-sm text-text-secondary mt-0.5">Plantillas de indicaciones y medicamentos</p>
        </div>
        {canEdit && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
            <Plus size={16} /> Nueva plantilla
          </button>
        )}
      </div>

      {/* Búsqueda */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar plantilla..."
          className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
      </div>

      {/* Lista de plantillas */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
          <HeartPulse size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="font-medium text-text-primary">
            {search ? 'Sin resultados' : 'Sin plantillas registradas'}
          </p>
          {!search && canEdit && (
            <button onClick={openCreate} className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
              Nueva plantilla
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const isOpen = expandida === p.id;
            return (
              <div key={p.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setExpandida(isOpen ? null : p.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                    <HeartPulse size={16} className="text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-text-primary">{p.nombre}</p>
                    {p.descripcion && <p className="text-xs text-text-secondary mt-0.5 truncate">{p.descripcion}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {p.medicamentos?.length ? (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Pill size={12} /> {p.medicamentos.length} med.
                      </span>
                    ) : null}
                    {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-gray-50 pt-4 space-y-4">
                    {/* Medicamentos */}
                    {p.medicamentos?.length ? (
                      <div>
                        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">Medicamentos</p>
                        <div className="space-y-2">
                          {p.medicamentos.map((m, i) => (
                            <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3">
                              <Pill size={14} className="text-primary-500 mt-0.5 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-text-primary">{m.nombre}</p>
                                <p className="text-xs text-text-secondary mt-0.5">
                                  {[m.dosis, m.frecuencia, m.duracion, m.via ? `vía ${m.via}` : ''].filter(Boolean).join(' · ')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {/* Indicaciones */}
                    {p.indicaciones && (
                      <div>
                        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">Indicaciones</p>
                        <p className="text-sm text-text-primary whitespace-pre-wrap bg-gray-50 rounded-lg px-4 py-3">{p.indicaciones}</p>
                      </div>
                    )}

                    {/* Acciones */}
                    {canEdit && (
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => openEdit(p)}
                          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                          Editar
                        </button>
                        <button onClick={() => deleteMut.mutate(p.id)} disabled={deleteMut.isPending}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-100 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                          <Trash2 size={13} /> Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Dialog ────────────────────────────────────────────────────────── */}
      <Dialog.Root open={open} onOpenChange={v => { setOpen(v); if (!v) setEditing(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-xl focus:outline-none flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 pt-6 pb-0 flex-shrink-0">
              <Dialog.Title className="text-lg font-semibold text-text-primary">
                {editing ? 'Editar plantilla' : 'Nueva plantilla de tratamiento'}
              </Dialog.Title>
              <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                <X size={18} />
              </Dialog.Close>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
              <div className="overflow-y-auto px-6 py-5 space-y-5">

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Nombre <span className="text-red-500">*</span></label>
                  <input required value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                    placeholder="Ej. Tratamiento hipertensión leve" className={inputCls} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Descripción</label>
                  <input value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
                    placeholder="Resumen breve del tratamiento" className={inputCls} />
                </div>

                {/* Medicamentos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-text-primary">Medicamentos</label>
                    <button type="button" onClick={addMed}
                      className="flex items-center gap-1 text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors">
                      <Plus size={12} /> Manual
                    </button>
                  </div>
                  <VademecumPickerInline onSelect={addMedFromVademecum} />
                  {form.medicamentos.length === 0 ? (
                    <p className="text-xs text-text-secondary text-center py-4">
                      Busca en el vademecum o agrega manualmente
                    </p>
                  ) : (
                    <div className="space-y-3 mt-3">
                      {form.medicamentos.map((m, i) => (
                        <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-text-secondary truncate max-w-[70%]">
                              {m.nombre || `Medicamento ${i + 1}`}
                            </span>
                            <button type="button" onClick={() => removeMed(i)} className="text-red-400 hover:text-red-600 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </div>
                          <input value={m.nombre} onChange={e => setMed(i, 'nombre', e.target.value)}
                            placeholder="Nombre del medicamento" className={inputCls} />
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <ComboboxInput options={DOSIS_RAPIDAS} value={m.dosis}
                                onChange={v => setMed(i, 'dosis', v)}
                                placeholder="Dosis (ej. 1 tableta)" className={inputCls} />
                            </div>
                            <div>
                              <ComboboxInput options={FRECUENCIAS} value={m.frecuencia}
                                onChange={v => setMed(i, 'frecuencia', v)}
                                placeholder="Frecuencia (ej. cada 8h)" className={inputCls} />
                            </div>
                            <div>
                              <ComboboxInput options={DURACIONES} value={m.duracion}
                                onChange={v => setMed(i, 'duracion', v)}
                                placeholder="Duración (ej. 7 días)" className={inputCls} />
                            </div>
                            <div>
                              <ComboboxInput options={VIAS} value={m.via}
                                onChange={v => setMed(i, 'via', v)}
                                placeholder="Vía (ej. oral)" className={inputCls} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Indicaciones */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Indicaciones generales</label>
                  <textarea value={form.indicaciones} onChange={e => setForm(p => ({ ...p, indicaciones: e.target.value }))}
                    rows={4} placeholder="Instrucciones, recomendaciones de estilo de vida, dieta, etc."
                    className={`${inputCls} resize-none`} />
                </div>

              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                  Cancelar
                </Dialog.Close>
                <button type="submit" disabled={isPending}
                  className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
                  {isPending ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear plantilla'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

    </div>
  );
}
